<script setup lang="ts">
import { state, setMode, setZoom, rotate } from '../lib/store'
import { MODE_LABELS } from '../lib/modes'
import type { SnipMode } from '../types'

const modes: { key: SnipMode; glyph: string; hint: string }[] = [
  { key: 'text', glyph: 'T', hint: 'Text — extract text into the cell' },
  { key: 'sum', glyph: 'Σ', hint: 'Sum — build =a+b+c from numbers' },
  { key: 'table', glyph: '▦', hint: 'Table — write a grid with borders' },
  { key: 'validation', glyph: '✔', hint: 'Validation — mark a check' },
  { key: 'exception', glyph: '✗', hint: 'Exception — mark a cross' },
]

function zoomBy(delta: number) {
  setZoom(Math.round((state.zoom + delta) * 100) / 100)
}
</script>

<template>
  <div class="toolbar">
    <div class="modes" role="group" aria-label="Snip mode">
      <button
        v-for="m in modes"
        :key="m.key"
        class="mode-btn"
        :class="{ active: state.mode === m.key }"
        :title="m.hint"
        :aria-pressed="state.mode === m.key"
        @click="setMode(m.key)"
      >
        <span class="glyph">{{ m.glyph }}</span>
        <span class="lbl">{{ MODE_LABELS[m.key] }}</span>
      </button>
    </div>

    <div class="row controls">
      <label v-if="state.mode === 'sum' || state.mode === 'table'" class="notation" title="Decimal notation for number parsing">
        <input type="checkbox" :checked="state.euNotation" @change="state.euNotation = !state.euNotation" />
        EU (1.234,56)
      </label>
      <div class="spacer"></div>
      <button class="icon-btn" :class="{ on: state.commentMode }" title="Comment mode (Ctrl+G)" @click="state.commentMode = !state.commentMode">💬</button>
      <button class="icon-btn" :class="{ on: !state.showComments }" :title="state.showComments ? 'Hide comments' : 'Show comments'" @click="state.showComments = !state.showComments">👁</button>
      <button class="icon-btn" title="Rotate left" @click="rotate(-90)">⟲</button>
      <button class="icon-btn" title="Rotate right" @click="rotate(90)">⟳</button>
      <button class="icon-btn" title="Zoom out" @click="zoomBy(-0.25)">−</button>
      <span class="zoom">{{ Math.round(state.zoom * 100) }}%</span>
      <button class="icon-btn" title="Zoom in" @click="zoomBy(0.25)">+</button>
    </div>
  </div>
</template>

<style scoped>
.toolbar {
  border-bottom: 1px solid var(--line);
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: var(--bg);
}
.modes {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
}
.mode-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 5px 2px;
}
.mode-btn .glyph {
  font-size: 15px;
  line-height: 1;
}
.mode-btn .lbl {
  font-size: 10px;
}
.mode-btn.active {
  background: var(--brand);
  color: #fff;
  border-color: var(--brand);
}
.controls {
  gap: 8px;
}
.notation {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--muted);
}
.zoom {
  min-width: 38px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.icon-btn.on {
  background: var(--brand);
  color: #fff;
  border-color: var(--brand);
}
</style>
